import { useState, useEffect } from 'react';
import api from '@/api';
import { BarChart3, Loader2, X, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const SEVERITY_STYLES = {
  green: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800',
  amber: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
  red: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800',
};

const TYPE_LABELS = { area: 'Area', linear: 'Linear', count: 'Count' };
const TYPE_UNITS = { area: 'sq ft', linear: 'ft', count: 'ea' };

export default function FloorPlanComparisonReport({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/floor-plan/takeoff-comparison`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-card border border-border rounded-sm p-8 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading comparison...</span>
        </div>
      </div>
    );
  }

  const comparison = data?.comparison || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="comparison-report-modal">
      <div className="bg-card border border-border rounded-sm w-[720px] max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-heading text-foreground">Takeoff Comparison — AI vs Manual</h2>
          </div>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="close-comparison-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {comparison.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <BarChart3 className="w-8 h-8 text-muted-foreground/40 mb-2" />
              <p className="text-xs text-muted-foreground">No comparison data yet.</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">Run AI Takeoff and add manual measurements to see the comparison.</p>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {['green', 'amber', 'red'].map(sev => {
                  const count = comparison.filter(c => c.severity === sev).length;
                  const label = sev === 'green' ? '< 5% variance' : sev === 'amber' ? '5-10% variance' : '> 10% variance';
                  const Icon = sev === 'green' ? Minus : sev === 'amber' ? ArrowUp : ArrowUp;
                  return (
                    <div key={sev} className={`px-4 py-3 rounded-sm border text-center ${SEVERITY_STYLES[sev]}`}>
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Icon className="w-3.5 h-3.5" />
                        <span className="font-mono text-xl font-bold">{count}</span>
                      </div>
                      <p className="text-[10px] uppercase tracking-widest font-heading">{label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Comparison table */}
              <div className="border border-border rounded-sm overflow-hidden" data-testid="comparison-table">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-muted/30 border-b border-border">
                      <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Trade</th>
                      <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Type</th>
                      <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground text-right">AI Value</th>
                      <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground text-right">Manual</th>
                      <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground text-right">Diff</th>
                      <th className="px-3 py-2 text-[10px] font-heading uppercase tracking-widest text-muted-foreground text-right">Var %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {comparison.map((row, i) => (
                      <tr key={i} className="hover:bg-muted/10" data-testid={`comparison-row-${i}`}>
                        <td className="px-3 py-2 text-xs text-foreground">{row.trade}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                            row.type === 'area' ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' :
                            row.type === 'linear' ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800' :
                            'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800'
                          }`}>
                            {TYPE_LABELS[row.type] || row.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-right text-foreground">{row.ai_value} <span className="text-muted-foreground text-[9px]">{TYPE_UNITS[row.type]}</span></td>
                        <td className="px-3 py-2 text-xs font-mono text-right text-foreground">{row.manual_value} <span className="text-muted-foreground text-[9px]">{TYPE_UNITS[row.type]}</span></td>
                        <td className="px-3 py-2 text-xs font-mono text-right">
                          <span className={row.difference > 0 ? 'text-amber-600 dark:text-amber-400' : row.difference < 0 ? 'text-cyan-600 dark:text-cyan-400' : 'text-muted-foreground'}>
                            {row.difference > 0 ? '+' : ''}{row.difference}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-mono font-bold rounded-sm border ${SEVERITY_STYLES[row.severity]}`}>
                            {row.variance_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-[9px] text-muted-foreground mt-3 text-center">
                Comparison is based on summed values per trade and measurement type. AI-accepted measurements vs. manually-created measurements.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
